import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Divider } from 'primereact/divider';
import { Tooltip } from 'primereact/tooltip';
import { Dropdown } from 'primereact/dropdown';
import DealerDropdown from '../../../../components/common/DealerDropdown';
import { validateDateRange } from '../../../../utils/dateUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';
import ChassisDropdown from '../../../../components/common/ChassisDropdown';

const isDomesticOptions = [
    { key: 'domestic', value: 1 },
    { key: 'foreign', value: 2 }
];

export const ReportTypeEnum = {
    AllWarrantyClaims: 1,
    MostReplacedParts: 2,
    DealerChassisCosts: 3
};

export default function WarrantyReportsFilter({ onApplyFilters, onResetFilters, reportType, isDealer }) {
    const { th } = useLanguage();

    const [filterOpen, setFilterOpen] = useState(true);
    const [filters, setFilters] = useState({
        itemCode: '',
        chassisNumber: null,
        dealer: null,
        isDomestic: null,
        startDate: null,
        endDate: null
    });
    const [dateErrors, setDateErrors] = useState({ startDate: null, endDate: null });

    const toggleOpen = () => setFilterOpen((p) => !p);

    const setField = (field, value) => {
        const next = { ...filters, [field]: value };
        setFilters(next);

        if (field === 'startDate' || field === 'endDate') {
            const { startDate, endDate } = next;
            if (startDate && endDate) {
                const v = validateDateRange(startDate, endDate);
                const msg = v.isValid ? null : th('warrantyReports.filter.dateRangeError');
                setDateErrors({ startDate: msg, endDate: msg });
            } else {
                setDateErrors({ startDate: null, endDate: null });
            }
        }
    };

    const apply = () => {
        if (dateErrors.startDate || dateErrors.endDate) return;
        onApplyFilters?.({
            itemCode: filters.itemCode || undefined,
            chassisNumber: filters.chassisNumber || undefined,
            dealerId: isDealer ? undefined : filters.dealer?.id ?? undefined,
            isDomestic: filters.isDomestic ?? undefined,
            startDate: filters.startDate ?? undefined,
            endDate: filters.endDate ?? undefined
        });
    };

    const reset = () => {
        setFilters({
            itemCode: '',
            chassisNumber: null,
            dealer: null,
            isDomestic: 2,
            startDate: null,
            endDate: null
        });
        setDateErrors({ startDate: null, endDate: null });
        onResetFilters?.();
    };

    return (
        <div className="generic-table-filter">
            <div className="grid">
                <div className="col-12 md:col-6 flex justify-content-start">
                    <Button icon="pi pi-filter" label={th('warrantyReports.filter.advancedFilter')} onClick={toggleOpen} outlined={!filterOpen} />
                </div>
            </div>

            {filterOpen && (
                <div className="mt-3">
                    <Divider />
                    <div className="text-xl font-medium mb-3">{th('warrantyReports.filter.advancedFilters')}</div>

                    <div className="flex flex-wrap gap-2" style={{ alignItems: 'end' }}>
                        {reportType === ReportTypeEnum.MostReplacedParts ? (
                            <>
                                <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                                    <div className="field mb-2 p-1">
                                        <label className="block mb-1 text-sm">{th('warrantyReports.filter.itemCode')}</label>
                                        <input
                                            type="text"
                                            className="p-inputtext p-component w-full"
                                            value={filters.itemCode || ''}
                                            onChange={(e) => setField('itemCode', e.target.value)}
                                            placeholder={th('warrantyReports.filter.itemCodePlaceholder')}
                                        />
                                    </div>
                                </div>

                                <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                                    <div className="field mb-2 p-1">
                                        <label className="block mb-1 text-sm">{th('warrantyReports.columns.ChassisNo')}</label>
                                        <ChassisDropdown value={filters.chassisNumber} onChange={(val) => setField('chassisNumber', val)} className="w-full" />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                {!isDealer && (
                                    <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                                        <div className="field mb-2 p-1">
                                            <label className="block mb-1 text-sm">{th('warrantyReports.filter.dealer')}</label>
                                            <DealerDropdown value={filters.dealer} onChange={(val) => setField('dealer', val)} className="w-full" placeholder={th('warrantyReports.filter.dealerPlaceholder')} rowCount={20} />
                                        </div>
                                    </div>
                                )}
                                <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                                    <div className="field mb-2 p-1">
                                        <label className="block mb-1 text-sm">{th('warrantyReports.filter.domesticForeign')}</label>
                                        <Dropdown
                                            value={filters.isDomestic}
                                            options={isDomesticOptions.map((opt) => ({
                                                value: opt.value,
                                                label: th(`warrantyReports.filter.domestic.${opt.key}`)
                                            }))}
                                            onChange={(e) => setField('isDomestic', e.value)}
                                            placeholder={th('warrantyReports.filter.selectPlaceholder')}
                                            showClear
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                                    <div className="field mb-2 p-1">
                                        <label className="block mb-1 text-sm">
                                            {th('warrantyReports.filter.startDate')}
                                            {dateErrors.startDate && (
                                                <>
                                                    <i id="wr-start-date-err" className="pi pi-exclamation-triangle ml-2 text-red-500" style={{ fontSize: '0.75rem', cursor: 'help' }} />
                                                    <Tooltip target="#wr-start-date-err" content={dateErrors.startDate} position="top" />
                                                </>
                                            )}
                                        </label>
                                        <Calendar
                                            value={filters.startDate}
                                            onChange={(e) => setField('startDate', e.value)}
                                            dateFormat="dd/mm/yy"
                                            showIcon
                                            placeholder={th('warrantyReports.filter.datePlaceholder', { field: th('warrantyReports.filter.startDate') })}
                                            className={`w-full ${dateErrors.startDate ? 'p-invalid' : ''}`}
                                        />
                                    </div>
                                </div>
                                <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                                    <div className="field mb-2 p-1">
                                        <label className="block mb-1 text-sm">
                                            {th('warrantyReports.filter.endDate')}
                                            {dateErrors.endDate && (
                                                <>
                                                    <i id="wr-end-date-err" className="pi pi-exclamation-triangle ml-2 text-red-500" style={{ fontSize: '0.75rem', cursor: 'help' }} />
                                                    <Tooltip target="#wr-end-date-err" content={dateErrors.endDate} position="top" />
                                                </>
                                            )}
                                        </label>
                                        <Calendar
                                            value={filters.endDate}
                                            onChange={(e) => setField('endDate', e.value)}
                                            dateFormat="dd/mm/yy"
                                            showIcon
                                            placeholder={th('warrantyReports.filter.datePlaceholder', { field: th('warrantyReports.filter.endDate') })}
                                            className={`w-full ${dateErrors.endDate ? 'p-invalid' : ''}`}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex justify-content-start mt-3">
                        <Button label={th('warrantyReports.filter.reset')} icon="pi pi-refresh" className="p-button-outlined mr-2" onClick={reset} />
                        <Button label={th('warrantyReports.filter.apply')} icon="pi pi-check" onClick={apply} />
                    </div>
                </div>
            )}
        </div>
    );
}
